import { defineStyleConfig, extendTheme, theme as baseTheme } from '@chakra-ui/react';
import { getTenantByHost } from '@pelicargo/tenants';
import { RequestStatus } from '@pelicargo/types';
import { ShipmentStatus } from '@prisma/client';

import { Button, CustomRadio, SimpleField, SimpleTooltip, Table } from './components';
import card from './components/card';
import formError from './components/form-error';
import Heading from './components/heading';
import labeledValue from './components/labeled-value';
import RadioCard from './components/radio-card';
import tag from './components/tag';
import * as foundations from './foundations';

const label = {
  fontSize: 'xs',
  fontWeight: 600,
  lineHeight: '12px',
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
};

export const theme = extendTheme({
  ...foundations,
  colors: {
    ...foundations.colors,
    secondary: foundations.colors.blue,
    ...(getTenantByHost()?.customerTheme?.colors ?? {}),
  },
  fontSizes: {
    xs: '0.75rem',
    sm: '0.8125rem',
    md: '0.875rem',
    lg: '1rem',
    xl: '1.125rem',
    '2xl': '1.25rem',
    '3xl': '1.5rem',
    '4xl': '1.875rem',
    '5xl': '2.25rem',
    '6xl': '3rem',
    '7xl': '3.75rem',
    '8xl': '4.5rem',
    '9xl': '6rem',
  },
  textStyles: {
    // Body
    bodyRegular: {
      fontWeight: 400,
      fontSize: '16px',
      lineHeight: '28px',
      color: 'text.default',
    },
    bodyMedium: {
      fontWeight: 500,
      fontSize: '16px',
      lineHeight: '28px',
      color: 'text.default',
    },
    bodySemibold: {
      fontWeight: 600,
      fontSize: '16px',
      lineHeight: '28px',
      color: 'text.default',
    },
    // Body Small
    bodySmallRegular: {
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '24px',
      color: 'text.paragraph',
    },
    bodySmallMedium: {
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '24px',
      color: 'text.default',
    },
    bodySmallSemibold: {
      fontWeight: 600,
      fontSize: '14px',
      lineHeight: '24px',
      color: 'text.default',
    },
    // Caption
    captionRegular: {
      fontWeight: 400,
      fontSize: '13px',
      lineHeight: '20px',
      color: 'text.default',
    },
    captionMedium: {
      fontWeight: 500,
      fontSize: '13px',
      lineHeight: '20px',
      color: 'text.default',
    },
    captionSemibold: {
      fontWeight: 600,
      fontSize: '13px',
      lineHeight: '20px',
      color: 'text.default',
    },
    // Caption Small
    captionSmallRegular: {
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '16px',
      color: 'text.subtle',
    },
    captionSmallMedium: {
      fontWeight: 500,
      fontSize: '12px',
      lineHeight: '16px',
      color: 'text.default',
    },
    captionSmallSemibold: {
      fontWeight: 600,
      fontSize: '12px',
      lineHeight: '16px',
      color: 'text.default',
    },
    label,
  },
  breakpoints: {
    base: '100%',
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1440px',
  },
  components: {
    ...baseTheme.components,
    Card: card,
    Tag: tag,
    Table,
    LabeledValue: labeledValue,
    Button,
    CustomRadio,
    SimpleField,
    SimpleTooltip,
    FormLabel: defineStyleConfig({
      variants: {
        inputLabel: label,
      },
    }),
    FormError: formError,
    RadioCard,
    Heading,
    Modal: defineStyleConfig({
      baseStyle: {
        dialog: {
          maxWidth: '7xl',
        },
      },
    }),
  },
});

export const statusColorScheme = {
  [RequestStatus.New]: 'orange',
  [RequestStatus.QuotingInProgress]: 'orange',
  [RequestStatus.QuotingPending]: 'orange',
  [RequestStatus.QuotingComplete]: 'green',
  [RequestStatus.InfoPending]: 'orange',
  [RequestStatus.BookingRequested]: 'orange',
  [RequestStatus.BookingChange]: 'orange',
  [RequestStatus.BookingPending]: 'orange',
  [RequestStatus.BookingComplete]: 'blue',
  [RequestStatus.BookingCancel]: 'orange',
  [RequestStatus.Cancelled]: 'red',
} satisfies Record<RequestStatus, string>;

export const shipmentStatusColorScheme = {
  [ShipmentStatus.PENDING]: 'gray',
  [ShipmentStatus.PICKUP_SCHEDULED]: 'orange',
  [ShipmentStatus.PICKUP_COMPLETE]: 'green',
  [ShipmentStatus.AT_WAREHOUSE]: 'orange',
  [ShipmentStatus.AIRLINE_ACCEPTED]: 'green',
  [ShipmentStatus.OUT_FOR_DELIVERY]: 'green',
  [ShipmentStatus.DEPARTED]: 'green',
  [ShipmentStatus.ARRIVED]: 'green',
  [ShipmentStatus.DELIVERED]: 'green',
  [ShipmentStatus.ON_HOLD]: 'red',
} satisfies Record<ShipmentStatus, string>;
